import React, { useEffect, useState } from "react";
import { UserGroupIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";

/** ====== Thứ tự ưu tiên chức vụ ====== */
const PRIORITY_SUBTEXTS = [
  "chu-tich",
  "pho-chu-tich-thuong-truc",
  "pho-chu-tich",
  "truong-ban",
  "uy-vien",
  "van-phong",
  "ke-toan",
  "hoi-vien",
];

/** Utils: chuẩn hoá chuỗi về slug */
const toSlug = (str) => {
  if (!str) return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/** Utils: tính chỉ số ưu tiên */
const getPriorityIndex = (roleName) => {
  const slug = toSlug(roleName);

  // 1) match chính xác
  let idx = PRIORITY_SUBTEXTS.findIndex((sub) => slug === sub);
  if (idx !== -1) return idx;

  // 2) match tiền tố
  idx = PRIORITY_SUBTEXTS.findIndex((sub) => slug.startsWith(sub));
  if (idx !== -1) return idx;

  // 3) match includes
  idx = PRIORITY_SUBTEXTS.findIndex((sub) => slug.includes(sub));
  return idx === -1 ? Number.POSITIVE_INFINITY : idx;
};

/** Utils: format ngày */
const formatDate = (dateString) => {
  if (!dateString) return "----";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("vi-VN");
};

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const AdminCard = () => {
  const [admins, setAdmins] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  /** Lấy danh sách admin */
  const fetchAdmins = async () => {
    try {
      const res = await ApiCustom.get("/user-shows");
      const users = res?.data?.data?.data?.users || [];
      setAdmins(users.filter((u) => u.type === "admin"));
    } catch (err) {
      console.error("Lỗi khi tải danh sách admin:", err);
    } finally {
      setLoading(false);
    }
  };

  /** Lấy chi tiết admin */
  const fetchAdminDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await ApiCustom.get(`/user-show/${id}`);
      setSelectedAdmin(res?.data?.data?.data);
      setDetailOpen(true);
    } catch (err) {
      console.error("Lỗi khi tải chi tiết người dùng:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  /** Sắp xếp admin theo PRIORITY_SUBTEXTS */
  const sortedAdmins = [...admins].sort((a, b) => {
    const pa = getPriorityIndex(a?.role?.roleName);
    const pb = getPriorityIndex(b?.role?.roleName);
    if (pa !== pb) return pa - pb;
    return (a?.role?.roleName || "").localeCompare(
      b?.role?.roleName || "",
      "vi",
      { sensitivity: "base" }
    );
  });

  /** Cấu hình bảng */
  const headers = [
    {
      key: "userAvatar",
      label: "Ảnh đại diện",
      render: (item) => (
        <div className="w-10 h-10 rounded-full overflow-hidden mx-auto">
          <img
            src={
              item?.userAvatar ||
              `https://ui-avatars.com/api/?name=${item?.userFullName}&size=200&background=random&color=fff&bold=true`
            }
            alt={item?.userFullName}
            className="w-full h-full object-cover"
          />
        </div>
      ),
    },
    {
      key: "userFullName",
      label: "Họ tên / Mã",
      render: (item) => (
        <div>
          <p className="font-medium text-gray-900">{item.userFullName}</p>
          <p className="text-sm text-gray-500">{item.userCode}</p>
        </div>
      ),
    },
    {
      key: "role",
      label: "Chức vụ",
      render: (item) => (
        <span className="text-sm text-gray-700">
          {item.role?.roleName || "----"}
        </span>
      ),
    },
    { key: "userEmail", label: "Email" },
    { key: "userPhone", label: "Số điện thoại" },
    {
      key: "isApproved",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded ${
            item.isApproved
              ? "bg-green-100 text-green-700"
              : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {item.isApproved ? "Đã duyệt" : "Đang chờ"}
        </span>
      ),
    },
  ];

  return (
    <>
      {/* Card */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full sm:w-auto shadow-md flex flex-col items-center text-center p-5 rounded-2xl border border-gray-200 bg-white  transition-all cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-blue-50 rounded-full mb-3">
          <UserGroupIcon className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-lg text-black mb-1">Ban chấp hành</p>
        <p className="text-3xl font-bold">{admins.length}</p>
      </motion.div>

      {/* Modal danh sách */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-6xl mx-4 md:mx-0 rounded-lg p-6"
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Danh sách Ban chấp hành
              </h2>

              {loading ? (
                <p className="text-gray-500">Đang tải dữ liệu...</p>
              ) : (
                <CustomTableComponent
                  headers={headers}
                  data={sortedAdmins}
                  onRowClick={(item) => fetchAdminDetail(item.id)}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết */}
      <AnimatePresence>
        {detailOpen && selectedAdmin && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-xl mx-4 rounded-lg p-6"
            >
              <button
                onClick={() => setDetailOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Thông tin chi tiết
              </h2>
              {detailLoading ? (
                <p className="text-gray-500">Đang tải thông tin...</p>
              ) : (
                <div>
                  <div className="flex gap-4 items-center mb-5">
                    <img
                      src={
                        selectedAdmin.userAvatar ||
                        `https://ui-avatars.com/api/?name=${selectedAdmin.userFullName}`
                      }
                      alt="Avatar"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-base text-gray-900">
                        {selectedAdmin.userFullName}
                      </p>
                      <p className="text-sm text-gray-500">
                        Mã: {selectedAdmin.userCode}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                    <div>
                      <p className="text-gray-500">Chức vụ tại CCA</p>
                      <p>{selectedAdmin.role?.roleName || "----"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sinh nhật</p>
                      <p>
                        {selectedAdmin.dateOfBirth
                          ? formatDate(selectedAdmin.dateOfBirth)
                          : "----"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Số điện thoại</p>
                      <p>{selectedAdmin.userPhone || "----"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p>{selectedAdmin.userEmail || "----"}</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminCard;
